// Tab «События» of a contact (R-E8): the person's history by the events of each project — the event, its dates, the ticket's status,
// the offer and the entrance — from the tickets the person holds. Newest event first; events without a project go last.
import { formatDate, recordTitle, type RecordObj } from '@confdeck/core-shared';
import { Chip, CompactTable, navigate, recordHref, useLinked, useMeta, useRecord, useSync, useTypesTick } from '@confdeck/core-web';
import s from './conferences.module.css';

interface Row { ticket: RecordObj; event: RecordObj | undefined; offer: RecordObj | undefined }

export function ContactEvents({ recordId }: { recordId: string }) {
  const { store } = useSync();
  const meta = useMeta();
  const contact = useRecord(recordId);
  const types = ['ticket', 'event', 'conference', 'offer'].map((k) => meta.typeByKey(k));
  useTypesTick(types.filter((t) => !!t).map((t) => t!.id));
  const ticketIds = useLinked(contact, 'tickets');
  const [T, E] = types;
  if (!T || !E) return null;
  const one = (rec: RecordObj | undefined, typeId: string, key: string) => {
    const f = rec ? meta.field(typeId, key) : undefined;
    return rec && f ? store.record(store.linked(rec, f)[0] ?? '') : undefined;
  };
  const groups = new Map<string, { project: RecordObj | undefined; rows: Row[] }>();
  for (const tid of ticketIds) {
    const ticket = store.record(tid);
    if (!ticket) continue;
    const event = one(ticket, T.id, 'event');
    const project = one(event, E.id, 'conference');
    const g = groups.get(project?.id ?? '') ?? groups.set(project?.id ?? '', { project, rows: [] }).get(project?.id ?? '')!;
    g.rows.push({ ticket, event, offer: one(ticket, T.id, 'offer') });
  }
  if (!groups.size) return <p className={s.tplHint}>У контакта нет билетов: он ещё не участвовал в событиях.</p>;
  const starts = (r: Row) => String(r.event?.data.start_date ?? '');
  const list = [...groups.values()].sort((a, b) => (!a.project ? 1 : !b.project ? -1 : recordTitle(a.project, meta).localeCompare(recordTitle(b.project, meta), 'ru')));
  return (
    <div className={s.history}>
      {list.map((g) => {
        const events = new Set(g.rows.map((r) => r.event?.id).filter(Boolean)).size;
        return (
          <section key={g.project?.id ?? 'none'} aria-label={g.project ? recordTitle(g.project, meta) : 'Без проекта'}>
            <h3 className={s.tplHead}>{g.project ? recordTitle(g.project, meta) : 'Без проекта'} <span className={s.tplHint}>· событий: {events}</span></h3>
            <CompactTable label="История по событиям" cardsOnPhone rows={g.rows.sort((a, b) => (!starts(a) ? 1 : 0) - (!starts(b) ? 1 : 0) || starts(b).localeCompare(starts(a)))} rowKey={(r) => r.ticket.id}
              onOpen={(r) => navigate(recordHref(r.event?.id ?? r.ticket.id))}
              columns={[
                { key: 'event', label: 'Событие', render: (r) => (r.event ? recordTitle(r.event, meta) : '—') },
                { key: 'dates', label: 'Даты', render: (r) => [r.event?.data.start_date, r.event?.data.end_date].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).map((v) => formatDate(String(v))).join(' — ') || '—' },
                { key: 'ticket', label: 'Билет', render: (r) => { const t = meta.term(String(r.ticket.data.status ?? '')); return t ? <Chip color={t.color} dot>{t.name}</Chip> : '—'; } },
                { key: 'offer', label: 'Вариант участия', render: (r) => (r.offer ? recordTitle(r.offer, meta) : '—') },
                { key: 'came', label: 'Вход', render: (r) => (r.ticket.data.checked_in_at ? 'был' : '—') },
              ]} />
          </section>
        );
      })}
    </div>
  );
}
